// templateData.ts — design tokens, palettes, font pairs, aesthetics, mock creator data, and React context.
// In production, pass real creator data via the CreatorDataProvider's `value` prop.

import React from 'react';
import type { Palette, FontPair, Aesthetic, AxisValues, CreatorData, JournalPost } from './types';

// ── Palettes ──────────────────────────────────────────────────
export const PALETTES: Record<string, Palette> = {
  Driftwood: {
    primary: '#7a6f63', secondary: '#d8c8b2', tertiary: '#c89478',
    bg: '#efe7d7', surface: '#e0d4bd', ink: '#2a221b', headerInk: '#26201a',
    muted: '#8a7d6c', accent: '#7a6f63', accent2: '#c89478', accentInk: '#efe7d7',
  },
  Citrine: {
    primary: '#e0ff5a', secondary: '#0e0e0e', tertiary: '#f4c5d4',
    bg: '#dcf75e', surface: '#c8e74a', ink: '#0e0e0e', headerInk: '#0e0e0e',
    muted: '#3a3a2a', accent: '#0e0e0e', accent2: '#f4c5d4', accentInk: '#dcf75e',
  },
  Midnight: {
    primary: '#0c0c10', secondary: '#f5cea0', tertiary: '#b8a4e6',
    bg: '#0c0c10', surface: '#17171c', ink: '#f4f0e8', headerInk: '#f4f0e8',
    muted: '#9b9588', accent: '#f5cea0', accent2: '#b8a4e6', accentInk: '#0c0c10',
  },
  Cellar: {
    primary: '#7a1f2b', secondary: '#f1e9da', tertiary: '#8d9670',
    bg: '#f4ede0', surface: '#e8dec8', ink: '#1f1815', headerInk: '#7a1f2b',
    muted: '#6e5b4f', accent: '#7a1f2b', accent2: '#8d9670', accentInk: '#f4ede0',
  },
  Vermillion: {
    primary: '#ff4d1f', secondary: '#efeadc', tertiary: '#5c5e3a',
    bg: '#efeadc', surface: '#e3ddc9', ink: '#1a1614', headerInk: '#ff4d1f',
    muted: '#7a6f5c', accent: '#ff4d1f', accent2: '#5c5e3a', accentInk: '#efeadc',
  },
  Plume: {
    primary: '#5a3a8a', secondary: '#ece4f5', tertiary: '#f4c5d4',
    bg: '#ece4f5', surface: '#dccaee', ink: '#1f1a2a', headerInk: '#3a2456',
    muted: '#6e6478', accent: '#5a3a8a', accent2: '#f4c5d4', accentInk: '#fff',
  },
  Cocoa: {
    primary: '#6b4632', secondary: '#f0e6d4', tertiary: '#d49575',
    bg: '#efe5d2', surface: '#e0d3b9', ink: '#2a1f17', headerInk: '#6b4632',
    muted: '#7a6655', accent: '#6b4632', accent2: '#d49575', accentInk: '#fff',
  },
  Coastline: {
    primary: '#2a4a6b', secondary: '#ecedee', tertiary: '#e87d6e',
    bg: '#e8edf2', surface: '#d8e1ec', ink: '#0f1822', headerInk: '#2a4a6b',
    muted: '#5a6878', accent: '#2a4a6b', accent2: '#e87d6e', accentInk: '#fff',
  },
  Meadow: {
    primary: '#4a6346', secondary: '#f1ecd9', tertiary: '#e9c869',
    bg: '#eef0de', surface: '#dde2c5', ink: '#1d2418', headerInk: '#4a6346',
    muted: '#6c7a64', accent: '#4a6346', accent2: '#e9c869', accentInk: '#eef0de',
  },
};

// ── Font pairs ────────────────────────────────────────────────
export const FONT_PAIRS: Record<string, FontPair> = {
  Editorial: { display: "'Oswald', sans-serif", body: "'Cormorant Garamond', serif", mono: "'JetBrains Mono', monospace", displayTrack: '0.04em', displayWeight: 500 },
  Modernist: { display: "'Barlow Condensed', sans-serif", body: "'Source Serif Pro', serif", mono: "'JetBrains Mono', monospace", displayTrack: '0.02em', displayWeight: 500 },
  Vintage: { display: "'Bebas Neue', sans-serif", body: "'Playfair Display', serif", mono: "'JetBrains Mono', monospace", displayTrack: '0.06em', displayWeight: 400 },
  Heavy: { display: "'Anton', sans-serif", body: "'EB Garamond', serif", mono: "'JetBrains Mono', monospace", displayTrack: '0.02em', displayWeight: 400 },
  Couture: { display: "'Cormorant Garamond', serif", body: "'Inter', sans-serif", mono: "'JetBrains Mono', monospace", displayTrack: '0', displayWeight: 500 },
};

// ── Aesthetic presets ─────────────────────────────────────────
export const AESTHETICS: Record<string, Aesthetic> = {
  Editorial: { density: 'airy', radius: 'sharp', borders: 'open', typeWeight: 'regular', imageTreatment: 'clean', dropCaps: true },
  Minimal: { density: 'regular', radius: 'soft', borders: 'open', typeWeight: 'light', imageTreatment: 'clean', dropCaps: false },
  Vintage: { density: 'airy', radius: 'sharp', borders: 'open', typeWeight: 'regular', imageTreatment: 'grain', dropCaps: true },
  Bold: { density: 'compact', radius: 'sharp', borders: 'bordered', typeWeight: 'heavy', imageTreatment: 'duotone', dropCaps: false },
};

// ── Axis value lookup tables ───────────────────────────────────
export const AXIS_VALUES: AxisValues = {
  density: {
    airy: { sectionPad: 'clamp(56px, 8cqi, 112px)', stack: 'clamp(28px, 4cqi, 56px)' },
    regular: { sectionPad: 'clamp(40px, 6cqi, 80px)', stack: 'clamp(24px, 3cqi, 40px)' },
    compact: { sectionPad: 'clamp(32px, 4cqi, 56px)', stack: 'clamp(16px, 2.5cqi, 28px)' },
  },
  radius: { sharp: '0px', soft: '8px', pill: '9999px' },
  borders: { open: 'transparent', bordered: 'currentColor' },
  typeWeight: { light: 300, regular: 400, heavy: 600 },
};

// ── Style presets (palette + fontPair + aesthetic bundle) ─────
// Palette / fontPair use Design catalog names (COLOR_OPTIONS / FONT_PAIR_OPTIONS).
export const STYLE_PRESETS: Record<string, { label: string; desc: string; palette: string; fontPair: string; aesthetic: string }> = {
  Atelier: { label: 'Atelier', desc: 'Clean default Design palette', palette: 'Default', fontPair: 'Default', aesthetic: 'Editorial' },
  Editor: { label: 'Editor', desc: 'Bold headings, structured layout', palette: 'Cream + Cobalt', fontPair: 'Playfair Display + Inter', aesthetic: 'Editorial' },
  Acid: { label: 'Acid', desc: 'Lime + ink, runway-loud', palette: 'Lime + Ruby', fontPair: 'Bricolage Grotesque + Inter', aesthetic: 'Bold' },
  'Noir Modern': { label: 'Noir Modern', desc: 'Dark, modern minimal', palette: 'Dark', fontPair: 'Inter + Inter', aesthetic: 'Minimal' },
  Driftwood: { label: 'Driftwood', desc: 'Warm taupe, soft luxe', palette: 'Tobacco', fontPair: 'Fraunces + Inter', aesthetic: 'Editorial' },
  Romance: { label: 'Romance', desc: 'Lilac + plum, gentle', palette: 'Lavender', fontPair: 'Gloock + Inter', aesthetic: 'Editorial' },
  Coastal: { label: 'Coastal', desc: 'Slate blue, coral pop', palette: 'Blueberry', fontPair: 'Inter + Inter', aesthetic: 'Minimal' },
  Cocoa: { label: 'Cocoa', desc: 'Milky cocoa, vintage warmth', palette: 'Bronze', fontPair: 'Playfair Display + Inter', aesthetic: 'Vintage' },
  Meadow: { label: 'Meadow', desc: 'Sage + butter, soft natural', palette: 'Matcha', fontPair: 'Default', aesthetic: 'Editorial' },
};

// ── Placeholder image generator (SVG data-URI) ────────────────
export function ph(label: string, w = 600, h = 800, hue = '#ddd2c2', ink = '#766658'): string {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${w} ${h}'>
    <defs><pattern id='p' width='14' height='14' patternUnits='userSpaceOnUse' patternTransform='rotate(45)'>
      <rect width='14' height='14' fill='${hue}'/><rect width='1' height='14' fill='${ink}' opacity='.18'/></pattern></defs>
    <rect width='${w}' height='${h}' fill='url(#p)'/>
    <text x='50%' y='50%' text-anchor='middle' font-family='monospace' font-size='${Math.min(w, h) / 22}' fill='${ink}'>${label}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

// ── Mock creator data (used as fallback when real data not provided) ──
export const MOCK_CREATOR: CreatorData = {
  bio: {
    name: 'MARLO WREN',
    handle: '@marlowren',
    avatar: ph('portrait', 240, 240, '#d8c6b3'),
    tagline: 'Stylist & creative director based between Paris and Mexico City. Documenting clothes, rooms, and the soft middle ground.',
    sublabel: 'faith · wellness · farm life',
    location: 'Paris · CDMX',
    cta: 'Follow',
    cta2: 'Shop with me',
    social: [
      { platform: 'Instagram', url: '#' },
      { platform: 'Substack', url: '#' },
      { platform: 'YouTube', url: '#' },
    ],
  },
  highlights: [
    { id: 'h1', label: 'SS26', cover: ph('SS26', 300, 300, '#c8b9a5') },
    { id: 'h2', label: 'Paris', cover: ph('Paris', 300, 300, '#bfae98') },
    { id: 'h3', label: 'Wardrobe', cover: ph('Wardrobe', 300, 300, '#d3c2ad') },
    { id: 'h4', label: 'Apartment', cover: ph('Apartment', 300, 300, '#cabaa3') },
    { id: 'h5', label: 'Travel', cover: ph('Travel', 300, 300, '#c3b39d') },
    { id: 'h6', label: 'Press', cover: ph('Press', 300, 300, '#d6c5b0') },
    { id: 'h7', label: 'Atelier', cover: ph('Atelier', 300, 300, '#bdac96') },
  ],
  linksTitle: 'My Links',
  links: [
    { id: 'l1', title: 'Shop my closet', subtitle: '40+ pieces · vintage & resale', url: '#', thumb: ph('closet', 120, 120, '#cdbca7') },
    { id: 'l2', title: 'Substack: Soft Middle', subtitle: 'Weekly notes on getting dressed', url: '#', thumb: ph('letter', 120, 120, '#c7b6a1') },
    { id: 'l3', title: 'Spring capsule edit', subtitle: '12 pieces, hand-picked', url: '#', thumb: ph('capsule', 120, 120, '#d2c1ac') },
    { id: 'l4', title: 'Press kit', subtitle: 'For brand partnerships', url: '#', thumb: ph('press', 120, 120, '#bba996') },
    { id: 'l5', title: 'Latest YouTube: Paris haul', subtitle: '18 min · April', url: '#', thumb: ph('film', 120, 120, '#d0bfa9') },
  ],
  banners: {
    hero: { id: 'b-hero', title: 'The Spring Edit', subtitle: '', cta: 'Open lookbook', url: '#', image: ph('hero', 1200, 700, '#c4b39d') },
    promo: { id: 'b-promo', title: 'Now in Paris', subtitle: 'Studio visits open through May 24', cta: 'Book a visit', url: '#', image: ph('studio', 1200, 700, '#bda88f') },
    footer: { id: 'b-footer', title: 'Working on something with us?', subtitle: 'Brand partnerships, press, and editorial enquiries.', cta: 'Get in touch', url: '#', image: ph('contact', 1200, 700, '#bca893') },
  },
  highlightCards: [
    { id: 'hl-1', title: 'Studio diary', subtitle: '', cta: 'Read more', url: '#', image: ph('diary', 1200, 700, '#bea98c') },
    { id: 'hl-2', title: 'Coming to NYC', subtitle: '', cta: 'See dates', url: '#', image: ph('nyc', 1200, 700, '#c2ad95') },
  ],
  featuredCarousel: null,
  featuredCollections: [],
  carousels: [
    {
      id: 'c-edit',
      title: 'The Edit',
      subtitle: 'Loved & worn this season',
      type: 'shop',
      componentType: 'products-carousel',
      items: [
        { id: 'p1', title: 'Linen waistcoat', subtitle: 'AGNÈS', price: '€340', image: ph('look·01', 400, 520, '#d4c4b0'), url: '#' },
        { id: 'p2', title: 'Wide trouser', subtitle: 'TOTÊME', price: '€290', image: ph('look·02', 400, 520, '#c9b9a3'), url: '#' },
        { id: 'p3', title: 'Suede mary jane', subtitle: 'ALOHAS', price: '€220', image: ph('look·03', 400, 520, '#bfae98'), url: '#' },
        { id: 'p4', title: 'Cotton dress', subtitle: 'HOPE', price: '€380', image: ph('look·04', 400, 520, '#cdbca7'), url: '#' },
        { id: 'p5', title: 'Leather tote', subtitle: 'WANT', price: '€520', image: ph('look·05', 400, 520, '#c7b6a1'), url: '#' },
        { id: 'p6', title: 'Wool cardigan', subtitle: 'WEEKDAY', price: '€140', image: ph('look·06', 400, 520, '#d2c1ac'), url: '#' },
      ],
    },
    {
      id: 'c-read',
      title: 'Read this week',
      subtitle: 'From the journal',
      type: 'content',
      componentType: 'content',
      items: [
        { id: 'j1', title: 'On uniforms', subtitle: '04.21 · 6 min', image: ph('essay·01', 400, 520, '#cabaa3'), url: '#' },
        { id: 'j2', title: 'A small apartment, considered', subtitle: '04.14 · 9 min', image: ph('essay·02', 400, 520, '#c3b39d'), url: '#' },
        { id: 'j3', title: 'Mexico City, in three shops', subtitle: '04.07 · 7 min', image: ph('essay·03', 400, 520, '#bda88f'), url: '#' },
        { id: 'j4', title: 'Why I stopped wearing black', subtitle: '03.31 · 5 min', image: ph('essay·04', 400, 520, '#d6c5b0'), url: '#' },
        { id: 'j5', title: 'The case for fewer shoes', subtitle: '03.24 · 4 min', image: ph('essay·05', 400, 520, '#bdac96'), url: '#' },
      ],
    },
  ],
  products: [
    { id: 'sp1', name: 'Linen waistcoat', category: 'OUTERWEAR', price: '€340', image: ph('shop·01', 400, 520, '#d4c4b0') },
    { id: 'sp2', name: 'Wide trouser', category: 'BOTTOMS', price: '€290', image: ph('shop·02', 400, 520, '#c9b9a3') },
    { id: 'sp3', name: 'Suede mary jane', category: 'FOOTWEAR', price: '€220', image: ph('shop·03', 400, 520, '#bfae98') },
    { id: 'sp4', name: 'Cotton dress', category: 'DRESSES', price: '€380', image: ph('shop·04', 400, 520, '#cdbca7') },
    { id: 'sp5', name: 'Leather tote', category: 'BAGS', price: '€520', image: ph('shop·05', 400, 520, '#c7b6a1') },
    { id: 'sp6', name: 'Wool cardigan', category: 'KNITWEAR', price: '€140', image: ph('shop·06', 400, 520, '#d2c1ac') },
    { id: 'sp7', name: 'Silk slip', category: 'DRESSES', price: '€260', image: ph('shop·07', 400, 520, '#cabaa3') },
    { id: 'sp8', name: 'Pearl earrings', category: 'ACCESSORIES', price: '€95', image: ph('shop·08', 400, 520, '#d6c5b0') },
  ],
  journal: [
    { id: 'jp1', slug: 'on-uniforms', title: 'On uniforms', excerpt: 'Why the same outfit, on repeat, became my most honest signature.', body: 'Why the same outfit, on repeat, became my most honest signature.\n\nIt is not boredom — it is clarity.', category: 'STYLE', author: 'Marlo Wren', date: 'April 21, 2026', minutes: 6, image: ph('cover·01', 800, 1000, '#cabaa3') },
    { id: 'jp2', slug: 'a-small-apartment-considered', title: 'A small apartment, considered', excerpt: '48 square meters and the rituals that make them feel like enough.', body: '48 square meters and the rituals that make them feel like enough.', category: 'INTERIORS', author: 'Marlo Wren', date: 'April 14, 2026', minutes: 9, image: ph('cover·02', 800, 1000, '#c3b39d') },
    { id: 'jp3', slug: 'mexico-city-in-three-shops', title: 'Mexico City, in three shops', excerpt: 'Where I send everyone who visits, and what I always bring back.', body: 'Where I send everyone who visits, and what I always bring back.', category: 'TRAVEL', author: 'Marlo Wren', date: 'April 7, 2026', minutes: 7, image: ph('cover·03', 800, 1000, '#bda88f') },
    { id: 'jp4', slug: 'why-i-stopped-wearing-black', title: 'Why I stopped wearing black', excerpt: 'A confession from someone who built a closet on it.', body: 'A confession from someone who built a closet on it.', category: 'STYLE', author: 'Marlo Wren', date: 'March 31, 2026', minutes: 5, image: ph('cover·04', 800, 1000, '#d6c5b0') },
    { id: 'jp5', slug: 'the-case-for-fewer-shoes', title: 'The case for fewer shoes', excerpt: 'Six pairs, two seasons, no decisions before coffee.', body: 'Six pairs, two seasons, no decisions before coffee.', category: 'STYLE', author: 'Marlo Wren', date: 'March 24, 2026', minutes: 4, image: ph('cover·05', 800, 1000, '#bdac96') },
    { id: 'jp6', slug: 'a-wardrobe-at-33', title: 'A wardrobe at 33', excerpt: 'What I kept, what I let go of, what I bought once.', body: 'What I kept, what I let go of, what I bought once.', category: 'STYLE', author: 'Marlo Wren', date: 'March 17, 2026', minutes: 8, image: ph('cover·06', 800, 1000, '#d3c2ad') },
  ],
  press: [
    { id: 'pr1', outlet: 'VOGUE', headline: 'The new editorial creators redefining personal style', date: 'Mar 2026', url: '#' },
    { id: 'pr2', outlet: 'SSENSE', headline: 'In conversation: Marlo Wren on the politics of dressing well', date: 'Feb 2026', url: '#' },
    { id: 'pr3', outlet: 'THE GENTLEWOMAN', headline: 'A day with the stylist quietly shaping Paris', date: 'Jan 2026', url: '#' },
    { id: 'pr4', outlet: 'NYT STYLE', headline: 'Inside the wardrobes of five creatives we follow', date: 'Dec 2025', url: '#' },
    { id: 'pr5', outlet: 'i-D', headline: 'Six creators on what "getting dressed" means now', date: 'Nov 2025', url: '#' },
  ],
  about: {
    headshot: ph('headshot', 700, 900, '#c8b9a5'),
    bio: 'Marlo Wren is a stylist and creative director working at the intersection of editorial, archive, and the everyday.',
    story: [
      'I started Soft Middle in 2022 because every other place I wrote was asking for either irony or perfection, and neither felt true to how I actually got dressed.',
      'These pages are an open notebook: what I wore, what I bought, what I sent back, what a friend talked me out of.',
      'If you are reading: thank you. The work is better with you here.',
    ],
    values: [
      { title: 'Slow over fast', body: 'Pieces I keep five seasons, not five weeks.', image: ph('more·01', 480, 600, '#cabaa3') },
      { title: 'Vintage first', body: 'Archive and resale before retail. Always.', image: ph('more·02', 480, 600, '#c3b39d') },
      { title: 'Repair, then replace', body: 'A small tailor on every street I live on.', image: ph('more·03', 480, 600, '#bda88f') },
      { title: 'Real bodies', body: 'No retouching the rib of a knit or the curve of a leg.', image: ph('more·04', 480, 600, '#d6c5b0') },
      { title: 'Words about clothes', body: 'Photographs are not enough. I want to write the reason.', image: ph('more·05', 480, 600, '#cdbca7') },
    ],
    email: 'studio@marlowren.com',
    phone: '+33 1 42 00 00 00',
    address: 'Studio 4B · 12 rue de Sévigné · 75004 Paris',
    hours: 'Mon–Thu · 10–18 CET',
  },
  // Mock contact config — only used in demo / no-userId preview. Real creators
  // get this from the `contact_page` table (created via ContactMeModal).
  contact: {
    enabled: true,
    fields: ['Name', 'Email', 'Message'],
    creatorMail: 'studio@marlowren.com',
    titleText: '',
    buttonText: 'Send message',
    imageUrl: '',
  },
  recent: [
    { id: 'r1', kind: 'blog', title: 'On uniforms', meta: 'Essay · 6 min', date: 'April 21', image: ph('r·01', 600, 400, '#cabaa3') },
    { id: 'r2', kind: 'shop', title: 'Spring capsule edit', meta: '12 pieces', date: 'April 18', image: ph('r·02', 600, 400, '#c3b39d') },
    { id: 'r3', kind: 'youtube', title: 'Paris haul · April', meta: 'Video · 18 min', date: 'April 16', image: ph('r·03', 600, 400, '#bda88f') },
    { id: 'r4', kind: 'press', title: 'Vogue — The new editorial creators', meta: 'Press', date: 'April 10', image: ph('r·04', 600, 400, '#d6c5b0') },
    { id: 'r5', kind: 'blog', title: 'A small apartment, considered', meta: 'Essay · 9 min', date: 'April 14', image: ph('r·05', 600, 400, '#cdbca7') },
    { id: 'r6', kind: 'shop', title: 'New in the closet', meta: '8 pieces', date: 'April 8', image: ph('r·06', 600, 400, '#c7b6a1') },
    { id: 'r7', kind: 'youtube', title: 'Get ready with me · Studio', meta: 'Video · 11 min', date: 'April 5', image: ph('r·07', 600, 400, '#d2c1ac') },
    { id: 'r8', kind: 'blog', title: 'Mexico City, in three shops', meta: 'Essay · 7 min', date: 'April 7', image: ph('r·08', 600, 400, '#cabaa3') },
  ],
  linkInBioStack: [],
};

/** Which journal post is shown on `journalPost` (slug or id from URL / editor). */
export const JournalViewContext = React.createContext<{ activePostSlugOrId: string | null }>({
  activePostSlugOrId: null,
});

export function useJournalActivePostSlugOrId(): string | null {
  return React.useContext(JournalViewContext).activePostSlugOrId;
}

/** Resolve a post from the live list for detail layout. */
export function findJournalPostBySlugOrId(
  journal: JournalPost[],
  slugOrId: string,
): JournalPost | undefined {
  const key = decodeURIComponent(slugOrId.trim());
  if (!key) return undefined;
  const postIdFromKey =
    /^post-(\d+)$/i.exec(key)?.[1] || (/^\d+$/.test(key) ? key : "");
  return journal.find((p) => {
    const id = String(p.id || "").trim();
    const slug = (p.slug && String(p.slug).trim()) || "";
    const canonical = slug || (id ? `post-${id}` : "");
    if (canonical && canonical === key) return true;
    if (id && (id === key || id === postIdFromKey)) return true;
    if (slug && slug === key) return true;
    return false;
  });
}

// ── React context ─────────────────────────────────────────────
// Empty about data used as a placeholder for live creators while the DB fetch
// is in-flight. All fields are blank so AboutStory can detect the loading state
// without confusing it with genuine no-content creators (headshot, bio, story
// all empty → render skeleton instead of the Marlo Wren demo content).
const EMPTY_ABOUT: CreatorData['about'] = {
  headshot: '',
  bio: '',
  story: [],
  values: [],
  email: '',
  phone: '',
  address: '',
  hours: '',
};

// Provides creator data to all section components.
// Wrap MasterTemplateEditor (or its ancestor) with CreatorDataProvider
// and pass real user data to swap out mock content.
export const CreatorDataContext = React.createContext<CreatorData>(MOCK_CREATOR);

export function useCreatorData(): CreatorData {
  return React.useContext(CreatorDataContext);
}

/**
 * True while the live `useCreatorDataFromDB` fetch is still in flight for a real
 * creator on the public website. Section components read this to show skeletons
 * instead of empty-state UI when data simply hasn't arrived yet.
 * Default `false` — editor / mock mode never shows loading skeletons.
 */
export const CreatorDataLoadingContext = React.createContext<boolean>(false);
export function useCreatorDataLoading(): boolean {
  return React.useContext(CreatorDataLoadingContext);
}

/**
 * True while blog posts are still fetching after the main live fan-out has painted.
 * Journal sections should skeleton on this; home must not wait for it.
 */
export const CreatorJournalLoadingContext = React.createContext<boolean>(false);
export function useCreatorJournalLoading(): boolean {
  return React.useContext(CreatorJournalLoadingContext);
}

/**
 * Per-section loading: skeleton only when the global live fetch is in flight
 * AND this section still has no content. Lets bootstrap/`template_settings`
 * paint immediately while other sections keep their layout-shaped loaders.
 */
export function useCreatorSectionLoading(hasContent: boolean): boolean {
  const loading = useCreatorDataLoading();
  return Boolean(loading && !hasContent);
}

interface CreatorDataProviderProps {
  value?: Partial<CreatorData>;
  /**
   * Set to `true` when rendering a real creator's site so that mock Marlo Wren
   * data never bleeds through, even during the first render before
   * `value.bio.userId` / `value.bio.username` have populated from the DB.
   * In the dashboard demo / no-userId preview, omit this prop (defaults to false).
   */
  isLive?: boolean;
  /**
   * Website onboarding seed preview: never promote to live via bio identity.
   * Keeps MOCK highlights / carousels / banners populated while answers override bio.
   */
  forceMock?: boolean;
  children: React.ReactNode;
}

/** `${pageKey}/${sectionId}` from `OrderedSectionsRenderer` — drives per-section inline heading paths. */
export const TemplateSectionInstanceCtx = React.createContext<string | null>(null);

export function useTemplateSectionInstance(): string | null {
  return React.useContext(TemplateSectionInstanceCtx);
}

/** Dashboard template editor only: upload handler for slot-based template images (`bio-avatar`, etc.). */
export const TemplateImageUploadCtx = React.createContext<
  ((slot: string, file: File) => Promise<string | null>) | undefined
>(undefined);

export function useTemplateImageUpload():
  | ((slot: string, file: File) => Promise<string | null>)
  | undefined {
  return React.useContext(TemplateImageUploadCtx);
}

function emptyBannerSlot(id: "hero" | "promo" | "footer"): CreatorData["banners"]["hero"] {
  return {
    id: `banner-${id}`,
    title: "",
    subtitle: "",
    cta: "",
    url: "#",
    image: "",
  };
}

/** Keep hero/promo/footer slots defined when live or template_settings omit banners. */
export function resolveCreatorBanners(
  fallback: CreatorData["banners"],
  incoming?: Partial<CreatorData["banners"]> | null,
): CreatorData["banners"] {
  if (!incoming) return fallback;
  return {
    hero: incoming.hero ?? emptyBannerSlot("hero"),
    promo: incoming.promo ?? emptyBannerSlot("promo"),
    footer: incoming.footer ?? emptyBannerSlot("footer"),
    // Extra banners (past hero/promo/footer) are resolved by component id.
    // Dropping catalog made a just-added image vanish after Add.
    ...(incoming.catalog?.length ? { catalog: incoming.catalog } : {}),
  };
}

const EMPTY_BANNERS: CreatorData["banners"] = {
  hero: emptyBannerSlot("hero"),
  promo: emptyBannerSlot("promo"),
  footer: emptyBannerSlot("footer"),
};

export function CreatorDataProvider({
  value,
  isLive = false,
  forceMock = false,
  children,
}: CreatorDataProviderProps) {
  const merged = React.useMemo<CreatorData>(() => {
    if (!value) return MOCK_CREATOR;
    // `isLive` can be set by the parent immediately (before bio data loads from DB)
    // so we never rely solely on bio fields that arrive asynchronously.
    // `forceMock` wins — used by website onboarding seed preview.
    const isLiveCreator = forceMock
      ? false
      : isLive ||
        Boolean(value.bio?.userId?.trim() || value.bio?.username?.trim());
    const next: CreatorData = { ...MOCK_CREATOR, ...value };

    // For live creators use empty banner slots as the merge base so Marlo's
    // demo banners never flash while real ones are still fetching.
    next.banners = resolveCreatorBanners(
      isLiveCreator ? EMPTY_BANNERS : MOCK_CREATOR.banners,
      value.banners,
    );

    if (isLiveCreator) {
      // ── Fields that must never show Marlo Wren demo data on real sites ──

      // bio: the spread above copies value.bio wholesale — if value.bio is
      // undefined (template_settings has no saved bio yet), next.bio falls back
      // to MOCK_CREATOR.bio and Marlo Wren's name/avatar appear on a real site.
      // Keep username/userId (routing + fetching) but zero all visible display
      // fields so sections show skeletons instead.
      if (!value.bio) {
        next.bio = {
          name: "", handle: "", avatar: "", tagline: "",
          sublabel: "", location: "", cta: "", cta2: "", social: [],
          username: undefined, userId: undefined,
        };
      }

      // journal
      next.journal = Array.isArray(value.journal) ? value.journal : [];
      // contact: undefined while contact_page row is fetching → shows skeleton
      next.contact = value.contact;
      // about: empty so AboutStory shows a skeleton instead of Marlo's headshot
      next.about = value.about ?? EMPTY_ABOUT;
      // carousels: empty so ContentCarouselTabs skips to its loading skeleton
      next.carousels = value.carousels ?? [];
      // highlights: empty so TemplatePlatformHighlightFeed skips fake tabs
      next.highlights = value.highlights ?? [];
      // links: empty while stayuptodate rows are fetching
      next.links = value.links ?? [];
      next.linksTitle = value.linksTitle ?? "";
      next.linksCollectionName = value.linksCollectionName ?? "";
      // products: empty while shop rows are fetching
      next.products = value.products ?? [];
      // press: empty so real creators with no press don't show VOGUE/SSENSE mock entries
      next.press = value.press ?? [];
      // recent: empty while recent activity is fetching
      next.recent = value.recent ?? [];
      // highlightCards: empty while highlight components are fetching
      next.highlightCards = value.highlightCards ?? [];
      // featuredCarousel: null so CarouselSection waits for real data
      next.featuredCarousel = value.featuredCarousel ?? null;
      next.featuredCollections = value.featuredCollections ?? [];
      // linkInBioStack: empty while link-in-bio rows are fetching
      next.linkInBioStack = value.linkInBioStack ?? [];
    }
    if (next.bio?.username) {
      const u = next.bio.username.trim().toLowerCase();
      next.bio = { ...next.bio, username: u };
    }
    if (next.journal?.length) {
      next.journal = next.journal.map((p) => ({
        ...p,
        slug: (p.slug && String(p.slug).trim()) || `post-${p.id}`,
        body: p.body ?? '',
      }));
    }
    return next;
  }, [value, isLive, forceMock]);
  return React.createElement(CreatorDataContext.Provider, { value: merged }, children);
}
